import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BadgeCheck, Boxes, CalendarClock, MapPin, MessageCircle } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useSeo, useSettings } from "@/lib/shop";
import { rp, fmtDate } from "@/lib/format";
import { ProductGrid } from "@/components/site/ProductCard";
import { SectionHead, GridSkeleton, ErrorState, Empty } from "@/components/site/States";
import { PoBadge } from "@/components/site/Badges";
import { HeroSlider } from "@/components/site/HeroSlider";
import { BrandSlider } from "@/components/site/BrandSlider";

const get = (url, params) => () => api.get(url, { params }).then((r) => r.data);

function Hero() {
  const { data, isLoading } = useQuery({ queryKey: ["banners", "hero"], queryFn: get("/banners", { placement: "hero" }) });
  if (isLoading) return <div className="h-[520px] bg-[var(--bk-yellow)] halftone border-b-2 border-neutral-900" data-testid="hero-loading" />;
  return <HeroSlider banners={data} />;
}

function Perks() {
  const perks = [
    [Boxes, "Koleksi pilihan", "Mainan & perlengkapan hobi terkurasi"],
    [BadgeCheck, "Ready Stock", "Stok tampil sesuai ketersediaan"],
    [CalendarClock, "Info PO jelas", "DP, jadwal tutup & estimasi tiba"],
    [MessageCircle, "Pesan via WhatsApp", "Tanya & pesan langsung ke admin"],
    [MapPin, "Toko fisik", "Kunjungi kami di Pontianak"],
  ];
  return (
    <section className="container-x -mt-1 py-8" data-testid="perks-section">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {perks.map(([Icon, t, d], i) => (
          <div key={t} className={`rounded-xl bg-white border-2 border-neutral-900 p-4 ${i === 4 ? "col-span-2 md:col-span-1" : ""}`}>
            <Icon className="h-6 w-6 text-[var(--bk-orange)]" />
            <div className="font-heading font-bold mt-2 text-[15px]">{t}</div>
            <div className="text-xs text-neutral-600 mt-0.5">{d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const COLORS = ["#ffc400", "#ff7a00", "#1d4fa8", "#e8251f", "#111111", "#16a34a", "#7c3aed"];

function CategoryCards() {
  const { data, isLoading } = useQuery({ queryKey: ["categories"], queryFn: get("/categories") });
  return (
    <section className="container-x py-10" data-testid="home-categories">
      <SectionHead eyebrow="Kategori" title="Kategori Populer" action={<Link to="/kategori" className="text-sm font-bold underline" data-testid="home-all-categories">Semua kategori</Link>} />
      {isLoading ? <GridSkeleton n={4} /> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
          {(data || []).map((c, i) => (
            <Link key={c.id} to={`/kategori/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group sticker sticker-hover rounded-2xl overflow-hidden bg-white">
              <div className="aspect-[4/3] overflow-hidden relative" style={{ background: COLORS[i % COLORS.length] }}>
                {c.image && <img src={imgUrl(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover mix-blend-normal group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-3 border-t-2 border-neutral-900" style={{ background: i % 2 ? "#fff" : "#fff7d6" }}>
                <div className="font-heading font-bold text-sm leading-tight">{c.name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{c.product_count} produk</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function ProductRow({ title, eyebrow, params, link, color, testid }) {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["products", params], queryFn: get("/products", { limit: 8, ...params }) });
  return (
    <section className="container-x py-10" data-testid={testid}>
      <SectionHead eyebrow={eyebrow} title={title} color={color} action={<Link to={link} className="text-sm font-bold underline whitespace-nowrap" data-testid={`${testid}-more`}>Lihat semua</Link>} />
      {isLoading ? <GridSkeleton n={4} /> : isError ? <ErrorState onRetry={refetch} /> : data.items.length ? <ProductGrid items={data.items} /> : <Empty title="Belum ada produk" />}
    </section>
  );
}

function PoHighlight() {
  const { data } = useQuery({ queryKey: ["po", {}], queryFn: get("/po") });
  const items = (data?.items || []).filter((p) => ["open", "closing_soon", "coming_soon"].includes(p.po_info.status)).slice(0, 4);
  return (
    <section className="bg-neutral-900 text-white py-14 my-6 relative overflow-hidden" data-testid="home-po-corner">
      <div className="absolute inset-y-0 right-0 w-1/3 bg-[var(--bk-blue)] stripes hidden md:block" />
      <div className="container-x relative">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-md bg-[var(--bk-red)]">Fitur utama</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3">PRE-ORDER CORNER</h2>
            <p className="text-neutral-300 mt-2 max-w-lg text-sm sm:text-base">Amankan koleksi incaranmu. Lihat DP, jadwal tutup PO, dan estimasi kedatangan.</p>
          </div>
          <Link to="/pre-order" className="btn-brand" data-testid="home-po-more">Masuk PO Corner <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {items.map((p) => (
            <Link key={p.id} to={`/pre-order/${p.slug}`} data-testid={`home-po-card-${p.slug}`} className="group bg-white text-neutral-900 rounded-xl overflow-hidden border-2 border-white hover:border-[var(--bk-yellow)] transition-colors">
              <div className="aspect-square overflow-hidden relative">
                <img src={imgUrl(p.images?.[0])} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <PoBadge status={p.po_info.status} className="absolute left-2 top-2" />
              </div>
              <div className="p-3">
                <div className="font-heading font-semibold text-sm line-clamp-2">{p.name}</div>
                <div className="price font-bold mt-1">{rp(p.po_info.price)}</div>
                <div className="text-[11px] text-neutral-600">{p.po_info.close_at ? `Tutup ${fmtDate(p.po_info.close_at)}` : "Jadwal menyusul"}</div>
              </div>
            </Link>
          ))}
          {!items.length && <p className="text-neutral-400 col-span-full">Belum ada PO yang dibuka saat ini.</p>}
        </div>
      </div>
    </section>
  );
}

function StoreInfo() {
  const s = useSettings();
  return (
    <section className="container-x py-10" data-testid="home-store-info">
      <div className="grid md:grid-cols-2 gap-6 items-stretch">
        <div className="rounded-3xl bg-[var(--bk-orange)] border-2 border-neutral-900 p-7 sm:p-10 relative overflow-hidden">
          <div className="absolute inset-0 halftone opacity-40" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl text-white [text-shadow:2px_2px_0_#111]">MAMPIR KE TOKO!</h2>
            <p className="mt-4 font-semibold text-neutral-900 flex gap-2"><MapPin className="h-5 w-5 shrink-0" />{s.address}</p>
            <div className="mt-4 text-sm text-neutral-900 space-y-1">
              {(s.hours || []).map((h, i) => <div key={i}><b>{h.label}:</b> {h.value}</div>)}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" className="btn-wa" data-testid="home-wa-btn"><MessageCircle className="h-4 w-4" />Chat WhatsApp</a>
              <Link to="/kontak" className="btn-brand bg-white" data-testid="home-contact-btn">Lihat Lokasi</Link>
            </div>
          </div>
        </div>
        <div className="rounded-3xl overflow-hidden border-2 border-neutral-900 min-h-[280px] bg-neutral-100">
          {s.maps_embed && <iframe title="Lokasi toko" src={s.maps_embed} loading="lazy" className="w-full h-full min-h-[280px]" referrerPolicy="no-referrer-when-downgrade" />}
        </div>
      </div>
    </section>
  );
}

function PromoStrip() {
  const { data } = useQuery({ queryKey: ["promos"], queryFn: get("/promos") });
  if (!data?.length) return null;
  return (
    <section className="container-x py-10" data-testid="home-promos">
      <SectionHead eyebrow="Promo aktif" color="var(--bk-red)" title="Lagi Diskon!" action={<Link to="/promo" className="text-sm font-bold underline" data-testid="home-promo-more">Semua promo</Link>} />
      <div className="grid md:grid-cols-2 gap-4">
        {data.slice(0, 2).map((pr) => (
          <Link key={pr.id} to="/promo" data-testid={`home-promo-${pr.slug}`} className="group sticker sticker-hover rounded-2xl overflow-hidden bg-[var(--bk-red)] text-white grid grid-cols-5">
            <div className="col-span-3 p-6">
              <div className="font-display text-4xl">{pr.discount_type === "percent" ? `${pr.value}%` : rp(pr.value)}</div>
              <div className="font-heading font-bold text-lg mt-1">{pr.title}</div>
              <div className="text-xs mt-2 text-white/85">s.d. {fmtDate(pr.end_at)} · {pr.products.length} produk</div>
            </div>
            <div className="col-span-2 bg-white">
              <img src={imgUrl(pr.banner_image)} alt={pr.title} loading="lazy" className="h-full w-full object-cover" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  useSeo(null, "Bengkel Toys & Hobbies Pontianak — model kit, Gunpla, figure, diecast, TCG, Beyblade. Ready Stock & Pre-Order, pesan via WhatsApp.");
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandSlider />
      <Perks />
      <CategoryCards />
      <ProductRow testid="home-newest" eyebrow="Baru masuk" title="Produk Terbaru" params={{ sort: "newest" }} link="/katalog?sort=newest" />
      <ProductRow testid="home-bestseller" eyebrow="Terlaris" color="var(--bk-orange)" title="Produk Terlaris" params={{ sort: "bestseller" }} link="/katalog?sort=bestseller" />
      <ProductRow testid="home-ready" eyebrow="Siap kirim" title="Ready Stock" params={{ availability: "ready", sort: "newest" }} link="/katalog?availability=ready" />
      <PoHighlight />
      <PromoStrip />
      <StoreInfo />
    </div>
  );
}
